import { Link } from "react-router-dom";
import { ArrowRight, Terminal } from "lucide-react";
import { SERVICES } from "@/data/services";
import { ServiceCard } from "@/components/site/ServiceCard";
import { PortfolioCard } from "@/components/site/PortfolioCard";
import { CtaBanner } from "@/components/site/SiteLayout";
import { usePublicPortfolio } from "@/hooks/usePublicPortfolio";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/aa99b69d-72c0-48f6-8f74-1387bc38d4ab/images/c8de082cc4ed0e0bd63d0c4aac7f83886be678ff3cae6c498374735bb6f4f61b.jpeg";

const STATS = [
  { k: "6", v: "core services" },
  { k: "1 day", v: "reply to every inquiry" },
  { k: "<2s", v: "target load time" },
  { k: "100%", v: "built in-house" },
];

const Hero = () => (
  <section className="noise relative overflow-hidden">
    <div className="bg-grid grid-fade absolute inset-0" />
    <div className="glow-orb pulse-glow absolute -left-20 top-10 h-[28rem] w-[28rem]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 md:pt-24 lg:grid-cols-[1.1fr_1fr] lg:px-12 lg:pb-28">
      <div>
        <span className="rise inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-300">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_#00F0FF]" /> Web development studio + site operator
        </span>
        <h1 data-testid="home-hero-title" className="rise mt-7 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
          We build websites<br />that <span className="text-gradient">earn their keep.</span>
        </h1>
        <p className="rise mt-6 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg" style={{ animationDelay: "160ms" }}>
          Beall Digital Group designs and engineers fast, conversion-focused websites and web apps for growing businesses, and we own and operate our own portfolio of sites, so every recommendation is tested on our own revenue first.
        </p>
        <div className="rise mt-10 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "240ms" }}>
          <Link to="/contact" data-testid="hero-cta-contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
            Start a project <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/portfolio" data-testid="hero-cta-portfolio" className="inline-flex items-center justify-center rounded-full border border-white/10 px-7 py-3.5 text-sm font-medium text-slate-200 transition-colors duration-200 hover:border-cyan-400/50 hover:text-white">
            See sites we run
          </Link>
        </div>
      </div>
      <div className="rise relative" style={{ animationDelay: "200ms" }}>
        <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,240,255,0.35)]">
          <img src={HERO_IMG} alt="Glowing wireframe grid with floating code windows" className="aspect-[4/3] w-full object-cover" />
        </div>
        <div className="absolute -bottom-6 left-4 right-4 rounded-xl border border-white/10 bg-[#0B0F18]/85 p-4 backdrop-blur-xl sm:left-auto sm:right-6 sm:w-72">
          <div className="flex items-center gap-2 font-mono-tech text-xs text-slate-400">
            <Terminal className="h-3.5 w-3.5 text-cyan-400" /> deploy --prod
          </div>
          <p className="mt-2 font-mono-tech text-xs text-emerald-300">✓ build passed · lighthouse 98</p>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="border-y border-white/5 bg-[#0A0D15]">
    <div className="mx-auto grid max-w-7xl grid-cols-2 px-4 sm:px-6 md:grid-cols-4 lg:px-12">
      {STATS.map((s, i) => (
        <div key={s.v} data-testid={`home-stat-${i}`} className={`px-2 py-8 md:px-6 ${i > 0 ? "md:border-l md:border-white/5" : ""}`}>
          <p className="font-display text-3xl font-bold text-white">{s.k}</p>
          <p className="mt-1 text-sm text-slate-500">{s.v}</p>
        </div>
      ))}
    </div>
  </section>
);

const SectionHead = ({ eyebrow, title, to, linkLabel, testId }) => (
  <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      <p className="mb-3 font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">{eyebrow}</p>
      <h2 className="max-w-xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
    </div>
    <Link to={to} data-testid={testId} className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 transition-colors hover:text-white">
      {linkLabel} <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

export default function Home() {
  const { items } = usePublicPortfolio();
  return (
    <div data-testid="home-page">
      <Hero />
      <Stats />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
        <SectionHead eyebrow="// services" title="Everything you need to launch, sell and scale online." to="/services" linkLabel="All 6 services" testId="home-services-link" />
        <div className="grid gap-6 md:grid-cols-3 md:gap-8">
          {SERVICES.slice(0, 3).map((s, i) => <ServiceCard key={s.id} service={s} index={i} compact />)}
        </div>
      </section>
      <section className="relative border-y border-white/5 bg-[#0A0D15]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 md:py-24 lg:px-12">
          <div>
            <p className="mb-3 font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">// two_engines</p>
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">An agency with skin in the game.</h2>
          </div>
          <div className="space-y-6 text-slate-400">
            <p><span className="font-semibold text-white">For clients,</span> we plan, design, build and maintain websites and web apps with one accountable team from kickoff to launch.</p>
            <p><span className="font-semibold text-white">For ourselves,</span> we own and operate a growing set of content and tool sites. The hosting, SEO and conversion lessons from those sites go straight into your project.</p>
          </div>
        </div>
      </section>
      {items && items.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
          <SectionHead eyebrow="// portfolio" title="Sites we own and operate." to="/portfolio" linkLabel="View portfolio" testId="home-portfolio-link" />
          <div className="grid gap-6 md:grid-cols-3 md:gap-8">
            {items.slice(0, 3).map((it, i) => <PortfolioCard key={it.id} item={it} index={i} />)}
          </div>
        </section>
      )}
      <CtaBanner />
    </div>
  );
}
